"use client"

import { useTranslations } from "next-intl"

import { Duotone } from "@/components/marketplace/layout/duotone"
import { HeadingMark } from "@/components/marketplace/layout/heading-mark"
import { ArrowIcon, CarouselDots, Heading, LinkButton, Reel, Row, Stack, Text } from "@/components/ui"
import { useReelPagination } from "@/lib/hooks/use-reel-pagination"

interface CollectionShowcaseSlide {
  id: string
  title: string
  accent?: string
  sub?: string
  cta?: string
  href?: string
  image: string
  serveId?: string
}

export const CollectionShowcase = ({ slides }: { slides: CollectionShowcaseSlide[] }) => {
  const { ref, index, stops, goTo } = useReelPagination(slides.length)
  const t = useTranslations("marketplace.discovery")
  return (
    <section
      role="region"
      aria-roledescription="carousel"
      aria-label={t("featuredCollections")}
      className="rounded-12x border-line-subtle bg-surface @container overflow-hidden border"
    >
      <Reel ref={ref} gap="0" snap>
        {slides.map((slide) => (
          <div key={slide.id} data-nelo-serve={slide.serveId} className="flex w-full flex-col @3xl:flex-row @3xl:items-stretch">
            <div className="aspect-banner relative @3xl:aspect-auto @3xl:min-h-110 @3xl:w-1/2">
              <Duotone src={slide.image} alt={slide.title} sizes="(max-width: 768px) 100vw, 50vw" className="absolute inset-0" />
            </div>
            <Stack
              align="center"
              gap="350"
              className="rounded-t-12x bg-surface relative -mt-500 px-400 pt-500 pb-200 text-center @3xl:mt-0 @3xl:w-1/2 @3xl:items-start @3xl:justify-center @3xl:gap-400 @3xl:rounded-none @3xl:px-800 @3xl:py-700 @3xl:text-left"
            >
              <span className="bg-palette-accent hidden h-1 w-12 rounded-full @3xl:block" aria-hidden />
              <Heading
                as="h3"
                variant="displaySm"
                family="display"
                tone="default"
                balance
                className="motion-safe:animate-rise-in max-w-measure-sm @3xl:text-display-lg font-extrabold tracking-tight"
              >
                {slide.title}
                {slide.accent && <HeadingMark>{slide.accent}</HeadingMark>}
              </Heading>
              {slide.sub && (
                <Text as="p" variant="body" family="text" tone="subtle" className="motion-safe:animate-rise-in animation-delay-100 max-w-measure-sm @3xl:text-body-lg text-pretty">
                  {slide.sub}
                </Text>
              )}
              {slide.cta && (
                <LinkButton
                  href={slide.href}
                  size="md"
                  iconRight={<ArrowIcon size={16} />}
                  className="motion-safe:animate-rise-in animation-delay-200 mt-100 min-w-0 px-500 font-bold"
                >
                  {slide.cta}
                </LinkButton>
              )}
            </Stack>
          </div>
        ))}
      </Reel>
      {slides.length > 1 && (
        <Row justify="center" className="pt-300 pb-400">
          <CarouselDots tone="plain" count={stops} index={index} onChange={goTo} />
        </Row>
      )}
    </section>
  )
}
